import React from "react";
import { Box, Dialog, IconButton, Typography, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { Badge, Icon } from "../AgentAnalytics/ds";
import tokens from "../AgentAnalytics/designTokens";
import AD from "./adPreview/adTokens";
import AdVariantCardReadOnly from "./adPreview/AdVariantCardReadOnly";
import { AD_TYPE_LABEL, int, relativeDay } from "./formatters";

const PREVIEW_DIALOG_W = 1100;
const PREVIEW_DIALOG_VH = 0.9;
const AD_PREVIEW_W = 300;

const useStyles = makeStyles({
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 16px",
    background: AD.surface,
    borderBottom: `1px solid ${AD.border}`,
    flexShrink: 0,
  },
  headTitle: { fontSize: 14, fontWeight: 700, color: AD.text },
  headSub: { fontSize: 11.5, color: AD.textMuted, marginTop: 2 },
  body: {
    flex: 1,
    display: "flex",
    minHeight: 0,
    background: AD.borderSubtle,
  },
  previewPane: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "20px 16px 16px",
    background: AD.borderSubtle,
  },
  sidePane: {
    width: 320,
    flexShrink: 0,
    overflowY: "auto",
    padding: 20,
    background: AD.surface,
    borderLeft: `1px solid ${AD.border}`,
  },
  section: {
    fontFamily: tokens.fontBody,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".05em",
    textTransform: "uppercase",
    color: tokens.textHint,
    margin: "0 0 8px",
  },
  block: { marginBottom: 22 },
  statGrid: { display: "flex", flexWrap: "wrap", gap: 14 },
  stat: { minWidth: 76 },
  statValue: {
    fontFamily: tokens.fontHeading,
    fontWeight: 700,
    fontSize: 17,
    color: "#232D2F",
    fontVariantNumeric: "tabular-nums",
  },
  statLabel: {
    fontFamily: tokens.fontBody,
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: ".04em",
    textTransform: "uppercase",
    color: tokens.textHint,
    marginTop: 1,
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    padding: "7px 0",
    borderBottom: `1px solid ${tokens.divider}`,
    fontFamily: tokens.fontBody,
    fontSize: 12.5,
    color: tokens.textPrimary,
  },
  rowKey: { color: tokens.textSecondary },
  notLive: {
    fontFamily: tokens.fontBody,
    fontSize: 12.5,
    color: tokens.textSecondary,
    border: `1px dashed ${tokens.borderStrong}`,
    borderRadius: 8,
    padding: "12px 14px",
  },
  metaLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    marginTop: 12,
    fontFamily: tokens.fontBody,
    fontSize: 12.5,
    fontWeight: 700,
    color: tokens.link,
    textDecoration: "none",
  },
});

const statusTone = (status) => {
  const value = String(status || "").toUpperCase();
  if (value === "ACTIVE") return "success";
  if (value === "PAUSED") return "warning";
  if (value.includes("DISAPPROVED") || value.includes("REJECTED")) return "error";
  return "neutral";
};

const Stat = ({ classes, value, label }) => (
  <Box className={classes.stat}>
    <Typography component="div" className={classes.statValue}>{value}</Typography>
    <Typography component="div" className={classes.statLabel}>{label}</Typography>
  </Box>
);

const Row = ({ classes, label, value }) =>
  value === null || value === undefined || value === "" ? null : (
    <Box className={classes.row}>
      <span className={classes.rowKey}>{label}</span>
      <span>{value}</span>
    </Box>
  );

export default function AdCardDialog({ ad, onClose }) {
  const classes = useStyles();
  if (!ad) return null;

  const live = ad.liveAd || (ad.adId || ad.adEffectiveStatus || ad.adStatus ? ad : null);
  const status = live && (live.adEffectiveStatus || live.adStatus);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        style: {
          width: PREVIEW_DIALOG_W,
          maxWidth: "94vw",
          height: `${PREVIEW_DIALOG_VH * 100}vh`,
          borderRadius: AD.radiusLg,
          background: AD.borderSubtle,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <div
        data-testid="ad-card-dialog"
        style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}
      >
        <div className={classes.head}>
          <div>
            <Typography className={classes.headTitle}>Ad Preview</Typography>
            <Typography className={classes.headSub}>
              {ad.adName || "Generated ad"}
              {" · "}
              {AD_TYPE_LABEL[ad.adType] || ad.adType || "—"}
              {Number.isInteger(ad.variantIndex) ? ` · Variant ${ad.variantIndex + 1}` : ""}
            </Typography>
          </div>
          <IconButton size="small" onClick={onClose} data-testid="ad-card-close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className={classes.body}>
          <div className={classes.previewPane} data-testid="ad-card-preview">
            <div style={{ width: AD_PREVIEW_W, maxWidth: "100%" }}>
              <AdVariantCardReadOnly variant={ad} live={live} />
            </div>
          </div>

          <div className={classes.sidePane}>
            <Box className={classes.block}>
              <Typography component="div" className={classes.section}>Meta ad</Typography>
              {live ? (
                <>
                  <Box display="flex" alignItems="center" gridGap={8} mb={1.5}>
                    <Badge tone={statusTone(status)} subtle>{status || "—"}</Badge>
                    <Typography
                      component="span"
                      style={{ fontFamily: tokens.fontBody, fontSize: 11.5, color: tokens.textHint }}
                    >
                      Live {relativeDay(live.createdAt)}
                    </Typography>
                  </Box>

                  <Box className={classes.statGrid} data-testid="ad-card-stats">
                    <Stat classes={classes} value={int(live.spend)} label="Spend" />
                    <Stat classes={classes} value={int(live.impressions)} label="Impressions" />
                    <Stat classes={classes} value={int(live.reach)} label="Reach" />
                    <Stat classes={classes} value={int(live.clicks)} label="Clicks" />
                    <Stat classes={classes} value={int(live.messages)} label="Messages" />
                    <Stat classes={classes} value={int(live.leads)} label="Leads" />
                  </Box>

                  {live.previewLink ? (
                    <a
                      className={classes.metaLink}
                      href={live.previewLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="ad-card-meta-link"
                    >
                      <Icon name="external-link" size={14} />
                      See on Meta
                    </a>
                  ) : null}
                </>
              ) : (
                <Box className={classes.notLive} data-testid="ad-card-not-live">
                  Generated but never launched, so there is no Meta ad or performance data for it.
                </Box>
              )}
            </Box>

            <Box className={classes.block}>
              <Typography component="div" className={classes.section}>Details</Typography>
              <Row classes={classes} label="Status" value={ad.variantStatus} />
              <Row classes={classes} label="Format" value={ad.creativeFormat} />
              <Row classes={classes} label="Aspect ratio" value={ad.aspectRatio} />
              <Row classes={classes} label="Gender" value={ad.gender} />
              <Row classes={classes} label="Template" value={ad.templateId} />
              <Row classes={classes} label="Ad id" value={live && live.adId} />
            </Box>

            {ad.why ? (
              <Box className={classes.block}>
                <Typography component="div" className={classes.section}>Why this ad</Typography>
                <Typography
                  component="div"
                  style={{
                    fontFamily: tokens.fontBody,
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    color: tokens.textPrimary,
                  }}
                >
                  {ad.why}
                </Typography>
              </Box>
            ) : null}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
